<script setup lang="ts">
import type { MapLngLat } from '@/@types/map';
import * as maplibregl from 'maplibre-gl';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

/**
 * A stop name on a white card, hung above a marker. A DOM marker rather than a
 * symbol layer: MapLibre cannot draw a shadowed card behind text, nor render the
 * app font at its exact weight.
 */
const props = defineProps<{
  mapInstance: maplibregl.Map;
  position: MapLngLat;
  text: string;
  /**
   * Distance in px from the coordinate up to the bottom of the card, negative
   * once the spot found for the card is under the marker rather than over it.
   */
  clearance: number;
  /** Sideways shift in px, to shoulder the card off its marker or off an edge. */
  offsetX: number;
  /** The name of the selected stop: same type, ringed with the green of its pin. */
  selected: boolean;
}>();

const labelElem = ref<HTMLElement>();
let marker: maplibregl.Marker | undefined;

watch(
  () => props.position,
  position => marker?.setLngLat(position),
);
watch([() => props.offsetX, () => props.clearance], ([offsetX, clearance]) =>
  marker?.setOffset([offsetX, -clearance]),
);

onMounted(() => {
  marker = new maplibregl.Marker({
    element: labelElem.value,
    anchor: 'bottom',
    offset: [props.offsetX, -props.clearance],
  })
    .setLngLat(props.position)
    .addTo(props.mapInstance);
});

onBeforeUnmount(() => {
  marker?.remove();
});
</script>

<template>
  <div ref="labelElem" class="map-label" :class="{ 'map-label--selected': selected }">{{ text }}</div>
</template>

<style lang="scss" scoped>
.map-label {
  background-color: $color-white;
  // Borne by every card, transparent unless selected, so both measure the same.
  // Width mirrored by `LABEL_BORDER` in `MapCanvas`, which lays the cards out.
  border: 2px solid transparent;
  border-radius: $radius-tag-small;
  box-shadow: $shadow-bus;
  color: $color-text-dark;
  font: $text-bold-smaller;
  letter-spacing: $text-bold-smaller-spacing;
  line-height: 1;
  padding: 3px 6px;
  pointer-events: none;
  white-space: nowrap;

  &--selected {
    border-color: $color-primary;
    box-shadow: none;
  }
}
</style>
